/**
 * The thing the screen watches: is our evidence still only on this phone?
 *
 * `OutboxDrainer` knows how to send a batch. It deliberately knows nothing about when to
 * try, whether there is a radio, or how to describe the situation to a rep standing on a
 * roof. That is this file, and it is kept out of `@rush/sync-core` for the ADR-0001 §3.1
 * reason and out of the React tree for a plainer one: a state machine that decides whether
 * to tell somebody their photos are safe should be testable without rendering anything.
 *
 * No DOM in here either. Connectivity arrives as a function and ticks arrive as calls, so
 * the shell owns `window` events and timers and this owns the decisions.
 *
 * The one rule that shapes every branch below: **`synced` is the only state that may be
 * described as safe, and only `OutboxDrainer` can produce it.** This class never infers a
 * sync from a lack of errors, from being online, or from time passing.
 */

import type { DrainResult, OutboxDrainer } from '@rush/sync-core';

import type { FieldRepository, SyncCounts } from '../db/repository.js';

export type SyncState =
  /** No API to sync to. Not an error, and not something a rep can fix. See ADR-0002 §2. */
  | 'unconfigured'
  /** No connection. Capture still works; this is the expected state on most roofs. */
  | 'offline'
  /** Everything the server has been offered, it has confirmed. */
  | 'idle'
  | 'syncing'
  /** The last attempt did not land. Ops are intact and will go again. */
  | 'error';

export interface SyncSnapshot {
  readonly state: SyncState;
  readonly counts: SyncCounts;
  /** Epoch ms of the last drain that reached a server. */
  readonly lastSuccessAt?: number;
  readonly lastError?: string;
}

const NO_COUNTS: SyncCounts = {
  pending: 0,
  synced: 0,
  failed: 0,
  photosHeldLocally: 0,
  bytesHeldLocally: 0,
};

export interface SyncEngineOptions {
  readonly repository: FieldRepository;
  /**
   * Absent when there is no API base URL configured, which is the shipped state today.
   * Modelled as a missing collaborator rather than a flag so there is no code path where
   * something tries to drain into a server that does not exist.
   */
  readonly drainer?: OutboxDrainer | undefined;
  readonly isOnline: () => boolean;
  readonly now?: () => number;
}

export class SyncEngine {
  readonly #repository: FieldRepository;
  readonly #drainer: OutboxDrainer | undefined;
  readonly #isOnline: () => boolean;
  readonly #now: () => number;
  readonly #listeners = new Set<(snapshot: SyncSnapshot) => void>();

  #snapshot: SyncSnapshot;
  #inFlight: Promise<SyncSnapshot> | undefined;

  constructor(options: SyncEngineOptions) {
    this.#repository = options.repository;
    this.#drainer = options.drainer;
    this.#isOnline = options.isOnline;
    this.#now = options.now ?? (() => Date.now());
    this.#snapshot = { state: this.#restingState(), counts: NO_COUNTS };
  }

  snapshot(): SyncSnapshot {
    return this.#snapshot;
  }

  subscribe(listener: (snapshot: SyncSnapshot) => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  /**
   * Re-read the counts without touching the network.
   *
   * Called after every capture. The number of unsynced items is the one piece of status a
   * rep can act on — it is what tells them not to walk away from wifi yet — so it updates
   * on the local write rather than waiting for the next sync attempt to refresh it.
   */
  async refresh(): Promise<SyncSnapshot> {
    const counts = await this.#repository.syncCounts();
    return this.#publish({ ...this.#snapshot, counts, state: this.#stateFor(this.#snapshot) });
  }

  /**
   * Attempt one full drain.
   *
   * Never throws. A sync failure is an ordinary event in this product, and a screen that
   * unmounts because LTE dropped is worse than the dropped packet. Failures land in the
   * snapshot where the banner can show them.
   */
  async syncNow(): Promise<SyncSnapshot> {
    // Coalesce rather than queue. Three triggers can fire at once — the interval, the
    // online event, and a rep jabbing "Sync now" — and they all want the same thing.
    this.#inFlight ??= this.#run().finally(() => {
      this.#inFlight = undefined;
    });
    return await this.#inFlight;
  }

  /** Called by the shell on `online`/`offline`. Reconnecting drains immediately. */
  async handleConnectivityChange(): Promise<SyncSnapshot> {
    if (!this.#isOnline()) {
      return this.#publish({ ...this.#snapshot, state: 'offline' });
    }
    return await this.syncNow();
  }

  async #run(): Promise<SyncSnapshot> {
    const drainer = this.#drainer;
    if (drainer === undefined) {
      return this.#publish({ ...this.#snapshot, state: 'unconfigured', counts: await this.#counts() });
    }
    if (!this.#isOnline()) {
      return this.#publish({ ...this.#snapshot, state: 'offline', counts: await this.#counts() });
    }

    this.#publish({ ...this.#snapshot, state: 'syncing' });

    let result: DrainResult;
    try {
      result = await drainer.drainAll();
    } catch (error) {
      // The drainer is written not to throw, so this is a bug or a storage failure rather
      // than a bad network. Either way nothing was marked and the ops are intact.
      return this.#publish({
        ...this.#snapshot,
        state: 'error',
        counts: await this.#counts(),
        lastError: describe(error),
      });
    }

    const counts = await this.#counts();

    if (result.transportError) {
      return this.#publish({
        ...this.#snapshot,
        state: 'error',
        counts,
        lastError: 'Could not reach the server. Your work is queued on this device.',
      });
    }

    if (result.failed > 0) {
      // Terminal rejections. The queue is not draining and no amount of waiting fixes it,
      // so this stays visible rather than settling to a reassuring idle.
      return this.#publish({
        ...this.#snapshot,
        state: 'error',
        counts,
        lastSuccessAt: this.#now(),
        lastError: `${String(result.failed)} item(s) were rejected and need attention.`,
      });
    }

    const settled: SyncSnapshot = { state: 'idle', counts, lastSuccessAt: this.#now() };
    return this.#publish(settled);
  }

  async #counts(): Promise<SyncCounts> {
    try {
      return await this.#repository.syncCounts();
    } catch {
      // Counts are a display concern. Failing to read them must not turn into a failure to
      // report sync state at all.
      return this.#snapshot.counts;
    }
  }

  /** Where the machine sits when it is not mid-attempt. */
  #restingState(): SyncState {
    if (this.#drainer === undefined) return 'unconfigured';
    return this.#isOnline() ? 'idle' : 'offline';
  }

  /**
   * State for a counts-only refresh.
   *
   * A local capture must not erase a standing error — the failure it reported is still
   * true — but it should move a stale `offline` to `idle` if the radio came back, and it
   * must never invent an `idle` while a drain is running.
   */
  #stateFor(current: SyncSnapshot): SyncState {
    if (current.state === 'syncing' || current.state === 'error') return current.state;
    return this.#restingState();
  }

  #publish(snapshot: SyncSnapshot): SyncSnapshot {
    this.#snapshot = snapshot;
    for (const listener of this.#listeners) listener(snapshot);
    return snapshot;
  }
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
